import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import '../styles/AdminTheme.css';

const AdminTheme = () => {
  const [theme, setTheme] = useState({
    primary_color: '#007bff',
    secondary_color: '#6c757d',
    background_color: '#ffffff',
    hero_background: '#f8f9fa',
    hero_text_color: '#333333',
    header_background: '#f0f7f8',
    button_color: '#007bff',
    button_text_color: '#ffffff',
    visual_effect: 'none'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const visualEffects = useMemo(() => [
    { value: 'none', label: t('admin.theme.effectNone'), icon: '\u{1F6AB}' },
    { value: 'snow', label: t('admin.theme.effectSnow'), icon: '\u2744\uFE0F', description: t('admin.theme.effectSnowDesc') },
    { value: 'rain', label: t('admin.theme.effectRain'), icon: '\u{1F327}\uFE0F', description: t('admin.theme.effectRainDesc') },
    { value: 'hearts', label: t('admin.theme.effectHearts'), icon: '\u2764\uFE0F', description: t('admin.theme.effectHeartsDesc') },
    { value: 'leaves', label: t('admin.theme.effectLeaves'), icon: '\u{1F342}', description: t('admin.theme.effectLeavesDesc') },
    { value: 'stars', label: t('admin.theme.effectStars'), icon: '\u2B50', description: t('admin.theme.effectStarsDesc') },
    { value: 'bubbles', label: t('admin.theme.effectBubbles'), icon: '\u{1FAE7}', description: t('admin.theme.effectBubblesDesc') },
    { value: 'confetti', label: t('admin.theme.effectConfetti'), icon: '\u{1F38A}', description: t('admin.theme.effectConfettiDesc') },
    { value: 'fireflies', label: t('admin.theme.effectFireflies'), icon: '\u2728', description: t('admin.theme.effectFirefliesDesc') }
  ], [t]);

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      const response = await api.get('/theme');
      setTheme(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || t('errors.loadError'));
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTheme(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/theme', theme);
      alert(t('admin.theme.saveSuccess'));
      setSaving(false);
    } catch (err) {
      alert(err.response?.data?.message || t('admin.theme.saveError'));
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t('confirms.resetTheme'))) {
      return;
    }

    try {
      await api.post('/theme/reset');
      alert(t('admin.theme.resetSuccess'));
      fetchTheme();
    } catch (err) {
      alert(err.response?.data?.message || t('admin.theme.resetError'));
    }
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="admin-theme">
      <div className="page-header">
        <h1>{t('admin.theme.title')}</h1>
        <button onClick={() => navigate('/admin')} className="back-btn">
          {t('common.backToDashboard')}
        </button>
      </div>

      <div className="theme-container">
        <div className="theme-form">
          <h2>{t('admin.theme.homeColors')}</h2>

          <div className="color-section">
            <h3>{t('admin.theme.heroSection')}</h3>
            <div className="color-inputs">
              <div className="color-input-group">
                <label htmlFor="hero_background">{t('admin.theme.heroBackground')}</label>
                <div className="color-preview-wrapper">
                  <input
                    type="color"
                    id="hero_background"
                    name="hero_background"
                    value={theme.hero_background}
                    onChange={handleChange}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={theme.hero_background}
                    onChange={handleChange}
                    name="hero_background"
                    className="color-text"
                    placeholder="#f8f9fa"
                  />
                </div>
              </div>

              <div className="color-input-group">
                <label htmlFor="hero_text_color">{t('admin.theme.heroTextColor')}</label>
                <div className="color-preview-wrapper">
                  <input
                    type="color"
                    id="hero_text_color"
                    name="hero_text_color"
                    value={theme.hero_text_color}
                    onChange={handleChange}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={theme.hero_text_color}
                    onChange={handleChange}
                    name="hero_text_color"
                    className="color-text"
                    placeholder="#333333"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="color-section">
            <h3>{t('admin.theme.headerSection', 'Barre de navigation')}</h3>
            <div className="color-inputs">
              <div className="color-input-group">
                <label htmlFor="header_background">{t('admin.theme.headerBackground', 'Couleur du header')}</label>
                <div className="color-preview-wrapper">
                  <input
                    type="color"
                    id="header_background"
                    name="header_background"
                    value={theme.header_background}
                    onChange={handleChange}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={theme.header_background}
                    onChange={handleChange}
                    name="header_background"
                    className="color-text"
                    placeholder="#f0f7f8"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="color-section">
            <h3>{t('admin.theme.generalColors')}</h3>
            <div className="color-inputs">
              <div className="color-input-group">
                <label htmlFor="primary_color">{t('admin.theme.primaryColor')}</label>
                <div className="color-preview-wrapper">
                  <input
                    type="color"
                    id="primary_color"
                    name="primary_color"
                    value={theme.primary_color}
                    onChange={handleChange}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={theme.primary_color}
                    onChange={handleChange}
                    name="primary_color"
                    className="color-text"
                    placeholder="#007bff"
                  />
                </div>
              </div>

              <div className="color-input-group">
                <label htmlFor="secondary_color">{t('admin.theme.secondaryColor')}</label>
                <div className="color-preview-wrapper">
                  <input
                    type="color"
                    id="secondary_color"
                    name="secondary_color"
                    value={theme.secondary_color}
                    onChange={handleChange}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={theme.secondary_color}
                    onChange={handleChange}
                    name="secondary_color"
                    className="color-text"
                    placeholder="#6c757d"
                  />
                </div>
              </div>

              <div className="color-input-group">
                <label htmlFor="background_color">{t('admin.theme.backgroundColor')}</label>
                <div className="color-preview-wrapper">
                  <input
                    type="color"
                    id="background_color"
                    name="background_color"
                    value={theme.background_color}
                    onChange={handleChange}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={theme.background_color}
                    onChange={handleChange}
                    name="background_color"
                    className="color-text"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="color-section">
            <h3>{t('admin.theme.buttons')}</h3>
            <div className="color-inputs">
              <div className="color-input-group">
                <label htmlFor="button_color">{t('admin.theme.buttonColor')}</label>
                <div className="color-preview-wrapper">
                  <input
                    type="color"
                    id="button_color"
                    name="button_color"
                    value={theme.button_color}
                    onChange={handleChange}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={theme.button_color}
                    onChange={handleChange}
                    name="button_color"
                    className="color-text"
                    placeholder="#007bff"
                  />
                </div>
              </div>

              <div className="color-input-group">
                <label htmlFor="button_text_color">{t('admin.theme.buttonTextColor')}</label>
                <div className="color-preview-wrapper">
                  <input
                    type="color"
                    id="button_text_color"
                    name="button_text_color"
                    value={theme.button_text_color}
                    onChange={handleChange}
                    className="color-picker"
                  />
                  <input
                    type="text"
                    value={theme.button_text_color}
                    onChange={handleChange}
                    name="button_text_color"
                    className="color-text"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="color-section visual-effects-section">
            <h3>{t('admin.theme.visualEffects')}</h3>
            <p className="section-description">
              {t('admin.theme.effectsDescription')}
            </p>
            <div className="visual-effects-grid">
              {visualEffects.map((effect) => (
                <div
                  key={effect.value}
                  className={`effect-card ${theme.visual_effect === effect.value ? 'active' : ''}`}
                  onClick={() => setTheme(prev => ({ ...prev, visual_effect: effect.value }))}
                >
                  <span className="effect-icon">{effect.icon}</span>
                  <span className="effect-label">{effect.label}</span>
                  {effect.description && (
                    <span className="effect-description">{effect.description}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="action-buttons">
            <button
              onClick={handleSave}
              className="save-btn"
              disabled={saving}
            >
              {saving ? t('common.savingChanges') : t('common.saveChanges')}
            </button>
            <button
              onClick={handleReset}
              className="reset-btn"
            >
              {t('admin.theme.resetButton')}
            </button>
          </div>
        </div>

        <div className="theme-preview">
          <h2>{t('admin.theme.preview')}</h2>
          <div
            className="preview-hero"
            style={{
              backgroundColor: theme.hero_background,
              color: theme.hero_text_color
            }}
          >
            <h3>{t('admin.theme.previewTitle')}</h3>
            <p>{t('admin.theme.previewSubtitle')}</p>
            <button
              className="preview-button"
              style={{
                backgroundColor: theme.button_color,
                color: theme.button_text_color
              }}
            >
              {t('admin.theme.previewButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTheme;
